import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { Icon } from './Icon';
import { Heading } from './Typography';

// Accordion — stacked disclosure sections built on Radix Accordion.
// Closed shape: caller passes `items` (an array of `{ id, title,
// content, disabled? }`) and the controlled state via `value` /
// `onValueChange`. No sub-component composition.
//
// Axes:
//
//   multiple   boolean (default false). When false, only one item can
//              be open at a time; opening a second closes the first.
//              When true, items open and close independently. Maps to
//              Radix's `type` prop ("single" / "multiple").
//
//   items      Array<{ id: string, title: string,
//                      content: ReactNode, disabled?: boolean }>.
//              `id` is the Radix value used by `value`/`onValueChange`.
//              `title` is rendered inside `<Heading variant="title-sm">`
//              (typographic commitment of the primitive).
//              `content` is rendered as-is — caller picks the shape
//              (Text, form, list, table, anything).
//
//   value      string (when multiple=false) | string[] (when multiple=true).
//              The currently open item id(s). Always controlled —
//              caller owns the state. Empty string / empty array
//              means "none open".
//
//   onValueChange  (next) => void. Called with the new value(s).
//
// In single mode `collapsible` is forced to true: the Radix default
// of "one item must always be open" is rarely the intended UX. If a
// future need shows up, lift it as an axis.

const ACCORDION_ITEM_CLASSNAME = 'tw-border-b tw-border-border';
const ACCORDION_HEADER_CLASSNAME = 'tw-flex';
const ACCORDION_TRIGGER_CLASSNAME = 'tw-flex tw-flex-1 tw-items-center tw-justify-between tw-py-4 tw-text-left tw-text-foreground tw-transition-colors hover:tw-text-primary [&[data-state=open]>[data-icon-shell]]:tw-rotate-180';
const ACCORDION_ICON_SHELL_CLASSNAME = 'tw-shrink-0 tw-transition-transform tw-duration-200';
const ACCORDION_CONTENT_CLASSNAME = 'tw-overflow-hidden tw-text-foreground tw-transition-all data-[state=closed]:tw-animate-accordion-up data-[state=open]:tw-animate-accordion-down';

function Accordion({ multiple = false, items, value, onValueChange }) {
    const rootProps = multiple
        ? { type: 'multiple', value, onValueChange }
        : { type: 'single', collapsible: true, value, onValueChange };

    return (
        <AccordionPrimitive.Root {...rootProps}>
            {items.map(({ id, title, content, disabled }) => (
                <AccordionPrimitive.Item
                    key={id}
                    className={ACCORDION_ITEM_CLASSNAME}
                    disabled={disabled}
                    value={id}
                >
                    <AccordionPrimitive.Header className={ACCORDION_HEADER_CLASSNAME}>
                        <AccordionPrimitive.Trigger className={ACCORDION_TRIGGER_CLASSNAME}>
                            <Heading as="span" tone="inherit" variant="title-sm">{title}</Heading>
                            <span className={ACCORDION_ICON_SHELL_CLASSNAME} data-icon-shell>
                                <Icon icon="chevronDown" size="small" tone="inherit" />
                            </span>
                        </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    <AccordionPrimitive.Content className={ACCORDION_CONTENT_CLASSNAME}>
                        <div className="tw-pb-4">{content}</div>
                    </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
            ))}
        </AccordionPrimitive.Root>
    );
}

export { Accordion };
